import { AppState, GetTokenSilentlyOptions, RedirectLoginOptions } from "@auth0/auth0-react";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosRequestHeaders, AxiosInstance } from "axios";
import { DEFAULT_LOGIN_OPTIONS, DEFAULT_SILENT_TOKEN_OPTIONS_INTELLISIGHT_API } from "constants/auth-constants";
// ----------------------------------------------------------------------

// Following changes here -> https://github.com/axios/axios/issues/5573
interface AdaptAxiosRequestConfig extends AxiosRequestConfig {
  headers: AxiosRequestHeaders;
}

const apiInstance = axios.create({
  baseURL: process.env.REACT_APP_HOST_API_URL || "",
});

export const setupApiConnections = (getApiToken: (tokenOptions: GetTokenSilentlyOptions, reloginOnFailure: boolean) => Promise<string | undefined>,
  loginWithRedirect: (options?: RedirectLoginOptions<AppState>) => Promise<void>) => {
    setupAxiosInstance(getApiToken, apiInstance, DEFAULT_SILENT_TOKEN_OPTIONS_INTELLISIGHT_API, loginWithRedirect);
};


// Track which Axios instances have had interceptors set up
const axiosInterceptorsSetup = new WeakSet<AxiosInstance>();

const setupAxiosInstance = (
  getApiToken: (tokenOptions: GetTokenSilentlyOptions, reloginOnFailure: boolean) => Promise<string | undefined>,
  instance: AxiosInstance,
  tokenOptions: GetTokenSilentlyOptions,
  loginWithRedirect: (options?: RedirectLoginOptions<AppState>) => Promise<void>
) => {
  // TECHDEBT: Avoid setting up interceptors multiple times on the same instance
  if (axiosInterceptorsSetup.has(instance)) {
    return;
  }
  axiosInterceptorsSetup.add(instance);
  instance.interceptors.request.use(
    async (config: AdaptAxiosRequestConfig) => {
      if (process.env.NODE_ENV === "development") {
        console.log(`AXIOS - Getting token with options:`, JSON.stringify(tokenOptions));
      }
      let localToken = await getApiToken(tokenOptions, true);
      if (process.env.NODE_ENV === "development") {
        console.log(`AXIOS - Retrieved token.`, localToken);
      }
      config.headers = config.headers ?? {};
      config.paramsSerializer = {
        indexes: null,
      };

      if (!localToken) {
        console.error("No token received, request will not include authorization header.");
        return config;
      }

      config.headers.Authorization = `Bearer ${localToken!}`;

      if (process.env.NODE_ENV === "development") {
        if (localToken) {
          try {
            const claims = JSON.parse(atob(localToken.split('.')[1]));
            console.log(`Request ${config.url} with token:`, claims);
          } catch (err) {
            console.warn('Unable to decode JWT token:', err);
          }
        }
      }

      return config;
    },
    (error) => {
      console.error(error);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (res: AxiosResponse) => { 
      if (process.env.NODE_ENV === "development") {
        console.log(`AXIOS - Response from ${res?.config?.url}:`, res);
      }

      // TECH-DEBT: Needed based on where and how the API Instance is setup.
      return res?.data;
    },
    async (err: AxiosError) => {
      console.error(err);
      if (err.response?.status === 401) {
        console.error(err, 'Unauthorized, redirecting to login.');
        await loginWithRedirect(DEFAULT_LOGIN_OPTIONS);
      } else if (err.response?.status === 403) {
        console.error('Forbidden request:', err);
      }
      else if (err.response?.status === 500) {
        console.error('Internal server error:', err);
      } else if (err.response?.status === 404) {
        console.warn('Resource not found:', err);
      } else {
        console.error('Axios response error:', err);
      }
      return Promise.reject(err);
    }
  );
}

export default apiInstance;
